/**
 * Which reservation dialog is open, and what happens when it finishes
 * (BON-472).
 *
 * Lifted out of the panel because it is the one place that knows how an outcome
 * becomes feedback, and because the panel is a table, a search box and a banner
 * — not a state machine. The dialogs themselves report an outcome and know
 * nothing about toasts or refreshes.
 *
 * EVERY MUTATION THAT CHANGED SOMETHING REFRESHES THE LIST. `stage`, `expiresAt`
 * and `linkSendFailed` are all derived server-side and all move on a rotation, so
 * a row left alone after a successful resend is a row telling the next CSR
 * something that stopped being true.
 */
import { useCallback, useState } from 'react';

import type { ReservationRow } from '../../api/reservations';
import { useToast } from '../../shared/toast/toastContext';

import type { RotationAlert, RotationOutcome } from './reservationRotationOutcome';

export type ReservationDialogKind = 'resend' | 'edit' | 'release';

export interface OpenReservationDialog {
  kind: ReservationDialogKind;
  row: ReservationRow;
}

export interface ReservationRowActionsState {
  active: OpenReservationDialog | null;
  /** A finished action the CSR has to acknowledge. Outlives the dialog that
   *  caused it — the request already committed, so closing the form does not
   *  make the news go away. */
  alert: RotationAlert | null;
  open: (kind: ReservationDialogKind, row: ReservationRow) => void;
  close: () => void;
  dismissAlert: () => void;
  settle: (outcome: RotationOutcome) => void;
  /** Re-read the list while leaving whatever is open, open. The edit form keeps
   *  its failures inline, and a refusal about the reservation's state means the
   *  row behind that form is already stale. */
  refreshList: () => void;
}

export function useReservationRowActions(refresh: () => void): ReservationRowActionsState {
  const { showToast } = useToast();
  const [active, setActive] = useState<OpenReservationDialog | null>(null);
  const [alert, setAlert] = useState<RotationAlert | null>(null);

  const open = useCallback(
    (kind: ReservationDialogKind, row: ReservationRow) => setActive({ kind, row }),
    [],
  );
  const close = useCallback(() => setActive(null), []);
  const dismissAlert = useCallback(() => setAlert(null), []);

  const settle = useCallback(
    (outcome: RotationOutcome) => {
      setActive(null);
      if (outcome.toast) showToast({ message: outcome.toast, tone: 'success' });
      // Set even when it is null, so a second attempt clears the first's news.
      setAlert(outcome.alert ?? null);
      if (outcome.refresh) refresh();
    },
    [refresh, showToast],
  );

  return { active, alert, open, close, dismissAlert, settle, refreshList: refresh };
}
